import { Fragment, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { listReturnUrl } from '../lib/listReturn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { normalizeDate } from '../lib/dates';
import { usePrintingMachines, useAllMachines, machineOptions, usePaperTypes, useTopFinishes, useSheetsLookup, type LookupOption } from '../lib/lookups';
import { useAuth, can, ROLE } from '../lib/auth';
import { formatJc } from './Dashboard';
import { PantoneAutocomplete } from '../components/PantoneAutocomplete';

/**
 * Only what the user actually types or picks lives in here.
 *
 * totalBoxes and totalSheetWithWastage are NOT fields — they are arithmetic on
 * clientRequestedQuantity, ups and wastage. They used to be stored in this
 * object and re-synced by a useEffect, which meant every keystroke in the
 * quantity box rendered the form, ran the effect, set state again, and rendered
 * a second time. Worse, the two could disagree: any code path that set the
 * inputs without the effect having run yet read a stale total.
 *
 * They are computed at render time now (see `derive`) and folded back in only
 * at the moment of save, which is the only moment the server cares.
 */
interface JobForm {
  poRefNumber: string; productId: string; jobName: string;
  jobOrderDate: string; jobPoDate: string; deliveryDate: string;
  ups: string;
  // UI-only helpers — poQuantity is read from the PO, wastage is what
  // gets ADDED to (boxes ÷ ups) to produce totalSheetWithWastage on save.
  // clientRequestedQuantity drives the actual calculation instead of poQuantity.
  poQuantity: string; clientRequestedQuantity: string; wastage: string;
  dieCutting: string; selectedDie: string;
  paperType: string; sheetSize: string;
  laminationType: string;
  sheetCutSizeLength: string; sheetCutSizeWidth: string;
  machine: string; plate: string;
  jobStatus: string; remarks: string;
  machineFront: string; machineBack: string;
  machineFrontColor: string; machineBackColor: string;
  machineFront2: string; machineFront2Color: string;
  artWNo: string;
  orderType: string;
}

const today = () => new Date().toISOString().slice(0, 10);

const empty: JobForm = {
  poRefNumber: '', productId: '', jobName: '',
  jobOrderDate: today(), jobPoDate: '', deliveryDate: '',
  ups: '0',
  poQuantity: '', clientRequestedQuantity: '', wastage: '0',
  dieCutting: '', selectedDie: '',
  paperType: '', sheetSize: '',
  laminationType: '',
  sheetCutSizeLength: '', sheetCutSizeWidth: '',
  machine: '', plate: '0',
  jobStatus: 'In Progress', remarks: '',
  machineFront: '', machineBack: '',
  machineFrontColor: '', machineBackColor: '',
  machineFront2: '', machineFront2Color: '',
  artWNo: '',
  orderType: '',
};

function calculateBaseSheets(poQty: string, ups: string): number {
  const q = Number(poQty) || 0;
  const u = Math.max(1, Number(ups) || 0);
  return Math.max(0, Math.ceil(q / u));
}

/**
 * The whole quantity calculation, as one pure function of the typed inputs.
 *
 * Pure and synchronous on purpose: what the operator sees is what gets saved,
 * with no render in between where the two could differ.
 */
/** Wastage that asks for a second look: above this many sheets ... */
const WASTAGE_MIN_SHEETS = 500;
/** ... or this many when a pass is on CD-102 or the SM 6-colour, the big presses (the API allows the same). */
const WASTAGE_MIN_SHEETS_CD = 700;
/** CD-102 as the planner reads it: a machine named with CD and 102. */
const isCd102 = (name: string | undefined) => !!name && /CD/i.test(name) && /102/.test(name);
/** The SM 6-colour: a machine named with SM and 6-colour (the API reads it the same way). */
const isSm6 = (name: string | undefined) => !!name && /SM/i.test(name) && /6\s*-?\s*COL/i.test(name);
/** Which big press a machine is, for the wastage notes. */
const bigPress = (name: string | undefined) => (isCd102(name) ? 'CD-102' : isSm6(name) ? 'SM 6-colour' : null);
/** ... or, when the client's quantity is over this ... */
const WASTAGE_BIG_QTY = 5000;
/** ... above this share of the sheets the job needs (boxes / ups). */
const WASTAGE_PCT = 0.15;
/** Extra boxes (wastage x ups) above this share of the boxes ordered: the card is refused (the API refuses it too). */
const WASTAGE_MAX_PCT = 0.2;

function derive(f: Pick<JobForm, 'clientRequestedQuantity' | 'ups' | 'wastage'>) {
  const baseSheets = calculateBaseSheets(f.clientRequestedQuantity, f.ups);
  const totalSheetWithWastage = baseSheets + (Number(f.wastage) || 0);
  const totalBoxes = totalSheetWithWastage * (Number(f.ups) || 0);
  return { baseSheets, totalSheetWithWastage, totalBoxes };
}

interface LinkedPo {
  id: string;
  label: string;
  quantity: string;
  date: string;
}

interface PoOption {
  id: string;
  label: string;
  /** The PO's own date, as stored (usually yyyy-mm-dd). */
  poDate?: string | null;
  hasJc?: number;
  /** Every job card on it is closed — listed only when its full number is typed. */
  closed?: number;
}

/**
 * /api/job-orders/club-check: may these POs share one job card?  The same
 * product always may; different products only when their Product Master
 * specs match — the mismatches list what differs.
 */
interface ClubCheck {
  main: { productId: string; productName: string } | null;
  pos: { poId: string; poNumber: string | null; productId: string | null; productName: string | null }[];
  mismatches: { productId: string; productName: string; poIds: string[]; diffs: { label: string; main: string; other: string }[] }[];
  missing: string[];
}
const clubUrl = (productId: string, poIds: string[]) =>
  `/api/job-orders/club-check?productId=${encodeURIComponent(productId)}&pos=${encodeURIComponent(poIds.join(','))}`;

/** The products a card's POs are for, its own first. */
function productsOnCard(c: ClubCheck | undefined): { id: string; name: string }[] {
  if (!c) return [];
  const out = new Map<string, { id: string; name: string }>();
  if (c.main) out.set(c.main.productId.toUpperCase(), { id: c.main.productId, name: c.main.productName });
  for (const p of c.pos) {
    if (p.productId && !out.has(p.productId.toUpperCase())) {
      out.set(p.productId.toUpperCase(), { id: p.productId, name: p.productName ?? p.productId });
    }
  }
  return [...out.values()];
}

/** "29 Sept 2026" from whatever the PO stored; '' when it has no date. */
function poDateLabel(raw: string | null | undefined): string {
  const iso = normalizeDate(raw);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** A PO as the dropdowns list it: number — job name · date. */
function poOptionText(p: PoOption): string {
  const date = poDateLabel(p.poDate);
  // The status first - the end of a long name is cut off in the dropdown.
  return `${p.closed ? '[Job card closed] ' : ''}${p.label}${date ? ` · ${date}` : ''}`;
}

/** What the API stores: the form's inputs plus the totals derived from them. */
type JobDto = JobForm & { id: string; totalBoxes: string; totalSheetWithWastage: string };

/** Hold a value still for `ms` after the last change — one request per pause, not per keystroke. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function JobOrderFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  // Job cards are created and edited by Super Admin / VPPL only. Everyone else
  // opens the same page read-only — the API refuses the write regardless.
  const { user } = useAuth();
  const canEditJob = can(user, isEdit ? 'EDIT_JOB' : 'CREATE_JOB');
  const decodedId = id ? decodeURIComponent(id) : id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState<JobForm>(empty);
  const [error, setError] = useState<string | null>(null);
  const [additionalPos, setAdditionalPos] = useState<LinkedPo[]>([]);
  const [addPoQuery, setAddPoQuery] = useState('');
  const [showAddPo, setShowAddPo] = useState(false);
  // A PO refused for the club (its product's specs differ), with what differs.
  const [clubRefused, setClubRefused] = useState<(ClubCheck & { adding?: string }) | null>(null);
  const debouncedAddPoQuery = useDebounced(addPoQuery, 250);

  const machines = usePrintingMachines();
  // Names for what the card already carries, removed machines included.
  const allMachines = useAllMachines();
  const papers = usePaperTypes();
  const finishes = useTopFinishes();
  const sheets = useSheetsLookup();

  const existing = useQuery({
    enabled: isEdit,
    queryKey: ['job', decodedId],
    queryFn: () => api<JobDto & { linkedPos?: Array<{ PO_ID: string; PO_Number: string | null; PO_Date: string | null; PO_Quantity: string | null; Job_Name: string | null }> }>(`/api/job-orders/${encodeURIComponent(decodedId!)}`),
  });

  // Change Spec + Create New Job Card: when the user picks this reason on
  // the Reissue dialog, we land here in /new mode with ?respawnFrom=<parent>
  // so the form pre-fills from the parent job and the save is treated as
  // a respawn on the server (parent closed atomically, POs transferred).
  const respawnFromParam = searchParams.get('respawnFrom') ?? '';
  const isRespawn = !!respawnFromParam;
  const respawnReasonParam = searchParams.get('reason') ?? '';
  const respawnParent = useQuery({
    enabled: isRespawn,
    queryKey: ['job-respawn-parent', respawnFromParam],
    queryFn: () => api<JobDto & { linkedPos?: Array<{ PO_ID: string; PO_Number: string | null; PO_Date: string | null; PO_Quantity: string | null; Job_Name: string | null }> }>(`/api/job-orders/${encodeURIComponent(respawnFromParam)}`),
  });

  // For New Job: POs without an existing Job, plus the ?po= param if coming
  // from the PO page (partial fulfillment — same PO, multiple job cards).
  // For Edit Job: include the job's own PO even though it's "used".
  const linkedPo = searchParams.get('po') ?? '';
  const [poQuery, setPoQuery] = useState('');
  const debouncedPoQuery = useDebounced(poQuery, 250);
  const recentPos = useQuery({
    queryKey: ['pos-available-for-job', decodedId, existing.data?.poRefNumber, respawnParent.data?.poRefNumber, linkedPo, debouncedPoQuery],
    queryFn: () => {
      const sp = new URLSearchParams({ limit: '100' });
      if (debouncedPoQuery.trim()) sp.set('q', debouncedPoQuery.trim());
      // Include the parent's PO on a respawn — until the save closes the
      // parent, the PO is still "used" and would otherwise be filtered out
      // of the dropdown.
      const inc = existing.data?.poRefNumber || respawnParent.data?.poRefNumber || linkedPo;
      if (inc) sp.set('include', inc);
      return api<PoOption[]>(`/api/purchase-orders/available-for-job?${sp.toString()}`);
    },
    staleTime: 30_000,
  });

  const addPoResults = useQuery({
    enabled: showAddPo,
    queryKey: ['pos-available-for-add', debouncedAddPoQuery],
    queryFn: () => {
      const sp = new URLSearchParams({ limit: '25' });
      if (debouncedAddPoQuery.trim()) sp.set('q', debouncedAddPoQuery.trim());
      return api<PoOption[]>(`/api/purchase-orders/available-for-job?${sp.toString()}`);
    },
    staleTime: 30_000,
  });

  const linkedQty = searchParams.get('qty') ?? '';

  // Auto-select PO when navigating from PO page via ?po= param
  useEffect(() => {
    if (linkedPo && !isEdit && !form.poRefNumber) {
      const next: Partial<JobForm> = { poRefNumber: linkedPo };
      if (linkedQty) next.clientRequestedQuantity = linkedQty;
      setForm((f) => ({ ...f, ...next }));
    }
  }, [linkedPo, linkedQty, isEdit, form.poRefNumber]);

  const isReissue = searchParams.get('reissue') === '1';
  // Club Order / Quantity (Edit → Club Order / Quantity): attach another PO
  // or change the Client Requested Quantity and the Wastage, nothing else.
  // No approval for anyone, and not a reissue: the card keeps its status and
  // date, and prints with no change shown (the API keeps the card before as
  // a version).
  const isClubEdit = isEdit && !isReissue && searchParams.get('mode') === 'club';
  // A reissue opens only the quantity and wastage; Club Order those and the
  // clubbed POs.  Everything else is locked in both.
  const reissueLocked = isReissue;
  const locked = isReissue || isClubEdit;

  useEffect(() => {
    const d = existing.data;
    if (!d) return;
    setForm((f) => {
      const next = { ...f, ...d };
      next.clientRequestedQuantity = d.clientRequestedQuantity || d.totalBoxes || '0';
      const stored = Number(d.totalSheetWithWastage) || 0;
      next.wastage = String(Math.max(0, stored - calculateBaseSheets(next.clientRequestedQuantity, d.ups)));
      if (isReissue) {
        next.jobOrderDate = today();
        // A reissue puts a closed or dispatched card back in work (the API does too).
        if (['Closed', 'Dispatched'].includes((next.jobStatus ?? '').trim())) next.jobStatus = 'In Progress';
      }
      return next;
    });
    if (d.linkedPos && d.linkedPos.length > 1) {
      setAdditionalPos(
        d.linkedPos
          .filter((lp) => lp.PO_ID !== d.poRefNumber)
          .map((lp) => ({
            id: lp.PO_ID,
            label: `${lp.PO_Number ?? lp.PO_ID} — ${lp.Job_Name ?? ''}`,
            quantity: lp.PO_Quantity ?? '0',
            date: lp.PO_Date ?? '',
          })),
      );
    }
  }, [existing.data, isReissue]);

  // Respawn pre-fill: copy every spec from the parent so the new form
  // opens ready to save.  The user is free to change any field before
  // saving — that is the whole point of the flow.  Date is bumped to
  // today; status starts fresh; the id will be assigned on POST.
  useEffect(() => {
    const d = respawnParent.data;
    if (!d) return;
    setForm((f) => {
      const next = { ...f, ...d };
      next.jobOrderDate = today();
      next.jobStatus = 'In Progress';
      next.clientRequestedQuantity = d.clientRequestedQuantity || d.totalBoxes || '0';
      const stored = Number(d.totalSheetWithWastage) || 0;
      next.wastage = String(Math.max(0, stored - calculateBaseSheets(next.clientRequestedQuantity, d.ups)));
      // Don't carry parent's remarks into the new card — they belonged
      // to the old spec.  The change reason itself is rendered in its
      // own row on the printed card.
      next.remarks = '';
      return next;
    });
    if (d.linkedPos && d.linkedPos.length > 1) {
      setAdditionalPos(
        d.linkedPos
          .filter((lp) => lp.PO_ID !== d.poRefNumber)
          .map((lp) => ({
            id: lp.PO_ID,
            label: `${lp.PO_Number ?? lp.PO_ID} — ${lp.Job_Name ?? ''}`,
            quantity: lp.PO_Quantity ?? '0',
            date: lp.PO_Date ?? '',
          })),
      );
    }
  }, [respawnParent.data]);

  // Fetch the linked PO so PO Quantity is visible on the form (both when
  // editing an existing job and after picking a PO for a new job).
  const poDetails = useQuery({
    enabled: !!form.poRefNumber,
    queryKey: ['po-summary', form.poRefNumber],
    queryFn: () => api<{ quantity: string; jobCardedQty?: number; jobCards?: number }>(`/api/purchase-orders/${encodeURIComponent(form.poRefNumber)}`),
    staleTime: 60_000,
  });

  const revisions = useQuery({
    enabled: isEdit,
    queryKey: ['job-revisions', decodedId],
    queryFn: () => api<{ OldQuantity: string | null; NewQuantity: string | null; RevisedOn: string | null; UserName: string | null }[]>(`/api/job-orders/${encodeURIComponent(decodedId!)}/revisions`),
  });

  const canSeeHistory = user?.rollId === ROLE.SuperAdmin || user?.rollId === ROLE.VPPL;
  const history = useQuery({
    enabled: isEdit && canSeeHistory,
    queryKey: ['job-history', decodedId],
    queryFn: () => api<Array<{
      History_ID: number; JobOrder_date: string | null; JobPODate: string | null;
      Total_Boxes: string | null; TotalSheet_withWastage: string | null; Ups: string | null;
      Machine: string | null; Paper_Type: string | null; Sheet_Size: string | null;
      Lamination_Type: string | null; Die_Cutting: string | null; Remarks: string | null;
      Job_Staus: string | null; Changed_On: string | null; UserName: string | null;
      Change_Reason: string | null;
    }>>(`/api/job-orders/${encodeURIComponent(decodedId!)}/history`),
  });

  useEffect(() => {
    if (poDetails.data?.quantity) {
      setForm((f) => {
        const poQty = poDetails.data!.quantity;
        const jcQty = poDetails.data!.jobCardedQty ?? 0;
        const remaining = Math.max(0, (Number(poQty) || 0) - jcQty);
        const next = { ...f, poQuantity: poQty };

        if (!isEdit && !f.clientRequestedQuantity) {
          next.clientRequestedQuantity = jcQty > 0 ? String(remaining) : poQty;
        } else if (isEdit && !isClubEdit && f.clientRequestedQuantity === (existing.data?.totalBoxes || '0')) {
          // (Not on Club Order: it starts from the card's quantity and wastage
          // as the API reads them, so what it shows is what saves.)
          next.clientRequestedQuantity = poQty;
          const stored = Number(existing.data?.totalSheetWithWastage) || 0;
          next.wastage = String(Math.max(0, stored - calculateBaseSheets(poQty, f.ups)));
        }
        return next;
      });
    }
  }, [poDetails.data, isEdit, isClubEdit, existing.data]);

  // Derived during render — no effect, no second render, no chance of the
  // displayed total disagreeing with the one about to be saved.
  const totals = derive(form);

  const reissueReason = searchParams.get('reason') ?? '';
  // A direct edit (one of the month's allowance): its reason, from Job Orders.
  const directReason = isEdit && !reissueReason ? (searchParams.get('directReason') ?? '').trim() : '';

  const allPoIds = [form.poRefNumber, ...additionalPos.map((p) => p.id)].filter(Boolean);

  // Which product each PO is for, and whether they may share the card.
  const clubQ = useQuery({
    enabled: allPoIds.length > 1,
    queryKey: ['club-check', form.productId, allPoIds.join(',')],
    queryFn: () => api<ClubCheck>(clubUrl(form.productId, allPoIds)),
    staleTime: 30_000,
  });
  const club = allPoIds.length > 1 ? clubQ.data : undefined;
  const cardProducts = productsOnCard(club);
  const productOfPo = (poId: string) => club?.pos.find((p) => p.poId === poId);

  const save = useMutation({
    mutationFn: () =>
      api<{ id: string }>(isEdit ? `/api/job-orders/${encodeURIComponent(decodedId!)}` : '/api/job-orders', {
        method: isEdit ? 'PUT' : 'POST',
        // Club Order sends only what it may change; the API takes the rest
        // of the card as it stands and works out its sheets and boxes.
        body: JSON.stringify(isClubEdit ? {
          clubEdit: true,
          clientRequestedQuantity: form.clientRequestedQuantity,
          clubWastage: Math.max(0, Math.round(Number(form.wastage) || 0)),
          poRefNumbers: allPoIds,
        } : {
          ...form,
          // A card whose machines could not be loaded leaves them as they are.
          ...(isEdit && existing.data && !('machineFront2' in existing.data) ? {
            machineFront2: undefined, machineBack: undefined,
            machineFrontColor: undefined, machineFront2Color: undefined, machineBackColor: undefined, artWNo: undefined,
          } : {}),
          poRefNumbers: allPoIds,
          totalBoxes: String(totals.totalBoxes),
          totalSheetWithWastage: String(totals.totalSheetWithWastage),
          ...(reissueReason ? { changeReason: reissueReason } : {}),
          ...(directReason ? { directEditReason: directReason } : {}),
          // Server closes the parent and transfers the PO atomically when
          // these are present.  The specChangeReason lands on the new job
          // row and gets printed on the card.
          ...(isRespawn ? {
            respawnFrom: respawnFromParam,
            specChangeReason: respawnReasonParam,
          } : {}),
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['pos-available-for-job'] });
      navigate(listReturnUrl('/job-orders'));
    },
    onError: (e: unknown) => {
      const err = e as Error & { body?: { details?: { fieldErrors?: Record<string, string[]> } } };
      const msg = err.message;
      if (msg === 'club_specs_differ' && err.body) {
        setClubRefused(err.body as unknown as ClubCheck);
        setError('These products cannot share one job card — see what differs under Source PO.');
        return;
      }
      if (msg.startsWith('po_already_has_job:')) {
        const jid = msg.slice('po_already_has_job:'.length);
        setError(`This PO already has Job Order ${jid}. Create a new PO to make another job.`);
      } else if (msg.startsWith('wastage_over_limit:')) {
        const [extra = 0, max = 0, ordered = 0, sheets = 0, ups = 1, free = WASTAGE_MIN_SHEETS] = msg.slice('wastage_over_limit:'.length).split(':').map(Number);
        const fmt = (v: number) => v.toLocaleString('en-IN');
        setError(`${isEdit ? 'Job card not saved' : 'Job card not created'}: wastage ${fmt(sheets)} sheets × ${ups} ups = ${fmt(extra)} extra boxes, `
          + `${ordered ? ((extra / ordered) * 100).toFixed(2) : '—'}% of the ${fmt(ordered)} boxes ordered. `
          + `The most allowed is ${fmt(Math.max(free, Math.floor(max / Math.max(1, ups))))} sheets: ${fmt(free)} sheets${free === WASTAGE_MIN_SHEETS_CD ? ' on CD-102 / SM 6-colour' : ''}, or ${Math.round(WASTAGE_MAX_PCT * 100)}% of the boxes ordered if more (${fmt(max)} boxes = ${fmt(Math.floor(max / Math.max(1, ups)))} sheets).`);
      } else if (msg === 'edit_not_approved') {
        setError('Not saved: this card needs an approved edit request, or one of the team\'s direct edits - open it with Edit from Job Orders.');
      } else if (msg.startsWith('direct_edits_used:')) {
        setError(`Not saved: the VPPL team has used all ${msg.split(':')[1]} direct edits this month. Raise a Request from Job Orders for GM to approve.`);
      } else if (msg === 'club_nothing_changed') {
        setError('Nothing to save: attach another PO, or change the Client Requested Quantity or the Wastage.');
      } else if (msg === 'invalid_quantity') {
        setError('Enter the Client Requested Quantity as a whole number above 0.');
      } else if (msg.startsWith('po_not_found:')) {
        setError(`PO ${msg.slice('po_not_found:'.length)} was not found (deleted?). Remove it and attach another.`);
      } else if (msg.startsWith('qty_below_produced:')) {
        const [produced, newQty] = msg.slice('qty_below_produced:'.length).split(':');
        setError(`Cannot reduce quantity to ${Number(newQty).toLocaleString()} — already produced ${Number(produced).toLocaleString()}.`);
      } else if (msg === 'validation_failed' && err.body?.details?.fieldErrors) {
        const fields = Object.entries(err.body.details.fieldErrors)
          .map(([k, v]) => `${k}: ${(v as string[]).join(', ')}`)
          .join('; ');
        setError(`Validation failed — ${fields}`);
      } else {
        setError(msg);
      }
    },
  });

  const seededFromUrl = useRef(false);
  useEffect(() => {
    const po = searchParams.get('po');
    if (isEdit || !po || seededFromUrl.current) return;
    seededFromUrl.current = true;
    void selectPO(po);
    // selectPO is stable for this purpose; re-running on every render would
    // refetch the seed and stomp on anything already typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isEdit]);

  async function selectPO(poId: string) {
    if (!poId) return;
    setForm((f) => ({ ...f, poRefNumber: poId }));
    try {
      const seed = await api<{
        po: { id: string; jobName: string; client: string; quantity: string; artwork: string; date: string; deliveryDate: string; orderType?: string };
        product: {
          id: string; paperType: string; sheetSize: string; laminationType: string;
          sheetCutSizeLength: string; sheetCutSizeWidth: string; ups: string;
          machineFront: string; machineBack: string;
          machineFrontColor: string; machineBackColor: string;
          machineFront2: string; machineFront2Color: string;
          dieNumber: string; artwork: string;
          artWNo: string;
        } | null;
        pastJob: JobDto | null;
      }>(`/api/job-orders/seed/${encodeURIComponent(poId)}`);

      setForm((f) => {
        const next: JobForm = { ...f, poRefNumber: poId };
        next.jobName = seed.po.jobName;
        if (seed.po.orderType) next.orderType = seed.po.orderType;
        // The PO quantity seeds the INPUT the totals are computed from. It used
        // to be written straight into totalBoxes, which the sync effect then
        // immediately overwrote — so the seeded quantity never survived.
        if (seed.po.quantity) next.clientRequestedQuantity = seed.po.quantity;
        if (seed.po.date) next.jobPoDate = seed.po.date;
        if (seed.po.deliveryDate) next.deliveryDate = seed.po.deliveryDate;
        if (seed.pastJob) {
          // Fallback to past job for these fields if not present in product
          next.paperType = seed.pastJob.paperType || next.paperType;
          next.sheetSize = seed.pastJob.sheetSize || next.sheetSize;
          next.laminationType = seed.pastJob.laminationType || next.laminationType;
          next.sheetCutSizeLength = seed.pastJob.sheetCutSizeLength || next.sheetCutSizeLength;
          next.sheetCutSizeWidth = seed.pastJob.sheetCutSizeWidth || next.sheetCutSizeWidth;
          next.ups = seed.pastJob.ups || next.ups;
        }
        if (seed.product) {
          next.productId = seed.product.id;
          next.paperType = seed.product.paperType || next.paperType;
          next.sheetSize = seed.product.sheetSize || next.sheetSize;
          next.laminationType = seed.product.laminationType || next.laminationType;
          next.sheetCutSizeLength = seed.product.sheetCutSizeLength || next.sheetCutSizeLength;
          next.sheetCutSizeWidth = seed.product.sheetCutSizeWidth || next.sheetCutSizeWidth;
          next.ups = seed.product.ups || next.ups;
          next.machineFront = seed.product.machineFront;
          next.machine = seed.product.machineFront;
          next.machineFront2 = seed.product.machineFront2;
          next.machineBack = seed.product.machineBack;
          next.machineFrontColor = seed.product.machineFrontColor;
          next.machineFront2Color = seed.product.machineFront2Color;
          next.machineBackColor = seed.product.machineBackColor;
          next.dieCutting = seed.product.dieNumber || next.dieCutting;
          next.selectedDie = seed.product.artwork || next.selectedDie;
          next.artWNo = seed.product.artWNo || next.artWNo;
        }
        return next;
      });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function addLinkedPo(poId: string) {
    if (!poId || poId === form.poRefNumber || additionalPos.some((p) => p.id === poId)) return;
    try {
      // A PO of another product joins only when its Product Master specs
      // match the card's product; otherwise say what differs.
      const check = await api<ClubCheck>(clubUrl(form.productId, [...allPoIds, poId]));
      if (check.mismatches.length || check.missing.length) {
        setClubRefused({ ...check, adding: poId });
        setShowAddPo(false);
        setAddPoQuery('');
        return;
      }
      setClubRefused(null);
      const detail = await api<{ quantity: string }>(`/api/purchase-orders/${encodeURIComponent(poId)}`);
      const label = addPoResults.data?.find((p) => p.id === poId)?.label ?? poId;
      const seed = await api<{ po: { date: string; quantity: string } }>(`/api/job-orders/seed/${encodeURIComponent(poId)}`);
      const addedQty = Number(detail.quantity ?? seed.po.quantity ?? '0') || 0;
      setAdditionalPos((prev) => [...prev, {
        id: poId,
        label,
        quantity: detail.quantity ?? seed.po.quantity ?? '0',
        date: seed.po.date ?? '',
      }]);
      // Roll the new PO's quantity into the Client Requested Quantity so
      // base-sheets scales with the combined qty.  Without this the extra
      // sheets get pushed into Wastage — that was the "97500 boxes / 3400
      // actual / 3100 wastage" bug on job 81561.  The operator can still
      // edit CRQ afterwards if they only want a partial fulfilment.
      setForm((f) => {
        const current = Number(f.clientRequestedQuantity) || 0;
        return { ...f, clientRequestedQuantity: String(current + addedQty) };
      });
      setShowAddPo(false);
      setAddPoQuery('');
    } catch (e) {
      setError((e as Error).message);
    }
  }

  function removeLinkedPo(poId: string) {
    const removed = additionalPos.find((p) => p.id === poId);
    setAdditionalPos((prev) => prev.filter((p) => p.id !== poId));
    if (removed) {
      const removedQty = Number(removed.quantity) || 0;
      setForm((f) => {
        const current = Number(f.clientRequestedQuantity) || 0;
        return { ...f, clientRequestedQuantity: String(Math.max(0, current - removedQty)) };
      });
    }
  }

  function update<K extends keyof JobForm>(k: K, v: JobForm[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // Wastage above 500 sheets (700 on CD-102 / SM 6-colour) - or, when the client's quantity is over 5,000,
  // above 15% of the job's Total Sheet (boxes / ups) - needs a second look
  // before the card is saved.
  // A pass on CD-102 or the SM 6-colour (front, front 2 or back) allows 700 sheets instead of 500.
  const machineName = (id: string) =>
    (machines.data ?? []).find((o) => o.id === id)?.name ?? (allMachines.data ?? []).find((o) => o.id === id)?.name ?? id;
  const onBig = [form.machine || form.machineFront, form.machineFront2, form.machineBack]
    .map((id) => (id ? bigPress(machineName(id)) : null)).find(Boolean) ?? null;
  const minSheets = onBig ? WASTAGE_MIN_SHEETS_CD : WASTAGE_MIN_SHEETS;
  const minRule = `${minSheets.toLocaleString('en-IN')} sheets${onBig ? ` on ${onBig}` : ''}`;
  const wastageSheets = Number(form.wastage) || 0;
  const clientQty = Number(form.clientRequestedQuantity) || 0;
  const wastageBig = clientQty > WASTAGE_BIG_QTY;
  const wastageLimit = wastageBig ? Math.round(totals.baseSheets * WASTAGE_PCT) : minSheets;
  const wastageRule = wastageBig
    ? `${Math.round(WASTAGE_PCT * 100)}% of the ${totals.baseSheets.toLocaleString('en-IN')} total sheets`
    : minRule;
  const wastageHigh = wastageSheets > wastageLimit;
  const wastagePct = totals.baseSheets > 0 ? (wastageSheets / totals.baseSheets) * 100 : null;
  const [wastageWarn, setWastageWarn] = useState(false);
  // Counted in boxes: the extra boxes the wastage makes (sheets x ups) may not
  // be over 20% of the boxes ordered, or the card is not saved.  On an edit,
  // only when the save raises them - a card already over can still have other
  // details corrected.
  const upsN = Math.max(1, Number(form.ups) || 0);
  const extraBoxes = wastageSheets * upsN;
  const maxExtraBoxes = Math.floor(clientQty * WASTAGE_MAX_PCT);
  const maxWastageSheets = Math.floor(maxExtraBoxes / upsN);
  const savedExtraBoxes = isEdit && existing.data
    ? Math.max(0, (Number(existing.data.totalSheetWithWastage) || 0) - calculateBaseSheets(existing.data.clientRequestedQuantity, existing.data.ups))
      * Math.max(1, Number(existing.data.ups) || 0)
    : null;
  // Over 20%, but 500 sheets or less (700 on CD-102 / SM 6-colour): a small job can need that - warn, don't stop.
  const over20 = clientQty > 0 && extraBoxes > maxExtraBoxes;
  const wastageBlocked = over20 && wastageSheets > minSheets
    && (savedExtraBoxes == null || extraBoxes > savedExtraBoxes);
  const wastageWarns = wastageHigh || over20;
  // The most that saves: 500 sheets (700 on CD-102 / SM 6-colour), or 20% of the boxes ordered when that is more.
  const maxSavableSheets = Math.max(minSheets, maxWastageSheets);
  const n = (v: number) => v.toLocaleString('en-IN');
  const wastageBlockedText = () =>
    `${isEdit ? 'Job card not saved' : 'Job card not created'}: wastage ${n(wastageSheets)} sheets × ${upsN} ups = ${n(extraBoxes)} extra boxes, `
    + `${((extraBoxes / clientQty) * 100).toFixed(2)}% of the ${n(clientQty)} boxes ordered. `
    + `The most allowed is ${n(maxSavableSheets)} sheets: ${minRule}, or ${Math.round(WASTAGE_MAX_PCT * 100)}% of the boxes ordered if more (${n(maxExtraBoxes)} boxes = ${n(maxWastageSheets)} sheets).`;

  // Club Order: the POs the card came with (they stay), its quantity and wastage before.
  const cardPoIds = new Set([existing.data?.poRefNumber ?? '', ...(existing.data?.linkedPos ?? []).map((lp) => lp.PO_ID)].filter(Boolean));
  const clubAdded = additionalPos.filter((p) => !cardPoIds.has(p.id));
  const clubOldQtyText = existing.data?.clientRequestedQuantity || existing.data?.totalBoxes || '0';
  const clubOldQty = Number(clubOldQtyText);
  const clubOldWastage = existing.data
    ? Math.max(0, (Number(existing.data.totalSheetWithWastage) || 0) - calculateBaseSheets(clubOldQtyText, existing.data.ups))
    : 0;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.poRefNumber) { setError('PO Number is required.'); return; }
    if (isClubEdit && !clubAdded.length && clientQty === clubOldQty && wastageSheets === clubOldWastage) {
      setError('Nothing to save: attach another PO, or change the Client Requested Quantity or the Wastage.');
      return;
    }
    if (wastageBlocked) { setError(wastageBlockedText()); return; }
    if (wastageWarns) { setWastageWarn(true); return; }
    save.mutate();
  }

  if (isEdit && existing.isLoading) return <div className="p-6 text-slate-500">Loading…</div>;
  if (isRespawn && respawnParent.isLoading) return <div className="p-6 text-slate-500">Loading source job…</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-brand">
          {!canEditJob
            ? `Job Card — ${isEdit ? formatJc(decodedId!) : decodedId}`
            : isClubEdit
              ? `Club Order / Quantity — ${formatJc(decodedId!)}`
              : isEdit
              ? `Edit Job Order — ${formatJc(decodedId!)}`
              : isRespawn
                ? `New Job Card (Change Spec of ${formatJc(respawnFromParam)})`
                : 'New Job Order'}
        </h1>
        <Link to={listReturnUrl('/job-orders')} className="text-sm text-slate-600 hover:underline">← Back</Link>
      </div>

      {!canEditJob && (
        <div className="mb-4 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          View only — job cards are created and edited by Super Admin and VPPL.
        </div>
      )}

      {directReason && canEditJob && (
        <div className="mb-4 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <strong>Direct edit.</strong>&nbsp;Saving uses one of the VPPL team's direct edits this month. Reason: <em>{directReason}</em>
        </div>
      )}
      {isReissue && (
        <div className="mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <strong>Reissue mode.</strong>&nbsp;Only <strong>Client Requested Quantity</strong> and <strong>Wastage</strong> are editable.
          Everything else stays the same as the source job card — if any other spec needs to change, raise a fresh Job Order instead.
        </div>
      )}
      {isClubEdit && canEditJob && (
        <div className="mb-4 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <strong>Club Order / Quantity.</strong>&nbsp;Attach another PO with <em>+ Add another PO (clubbed order)</em>, or change
          the <strong>Client Requested Quantity</strong> and the <strong>Wastage</strong> — nothing else on the card changes.
          No approval is needed and it is not a reissue: the job card prints with no change shown, and the card before is kept in its versions.
        </div>
      )}

      {isRespawn && (
        <div className="mb-4 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <strong>Change Spec.</strong>&nbsp;Fields are pre-filled from <span className="font-mono">{formatJc(respawnFromParam)}</span>. Change what you need — UPS, sheet size, die, machine — then Save. On save the old job card will be closed and this one will get a brand new serial number.
          {respawnReasonParam && (
            <div className="mt-1 text-blue-800"><strong>Reason:</strong> {respawnReasonParam}</div>
          )}
        </div>
      )}

      {wastageWarn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setWastageWarn(false)}>
          <div className="w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="bg-amber-500 px-4 py-3 font-semibold text-white">⚠ High wastage</div>
            <div className="space-y-2 p-4 text-sm text-slate-700">
              <p>
                Wastage is <b>{wastageSheets.toLocaleString('en-IN')} sheets</b>
                {wastagePct != null && <> — <b>{wastagePct.toFixed(1)}%</b> of the {totals.baseSheets.toLocaleString('en-IN')} sheets the job needs</>}.
              </p>
              <p>
                The limit is <b>{wastageLimit.toLocaleString('en-IN')}</b> ({wastageRule}): {minRule}, or {Math.round(WASTAGE_PCT * 100)}% of the Total Sheet when the client's quantity is over 5,000.
                Check it is right before saving.
              </p>
              {over20 && (
                <p className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-red-800">
                  That is <b>{n(extraBoxes)} extra boxes</b> — {((extraBoxes / clientQty) * 100).toFixed(2)}% of the {n(clientQty)} boxes ordered, over {Math.round(WASTAGE_MAX_PCT * 100)}%.
                  It can still be saved because the wastage is {n(minSheets)} sheets or less{onBig ? ` (${onBig})` : ''}.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 px-4 py-3">
              <button type="button" className="btn-primary" autoFocus onClick={() => setWastageWarn(false)}>Go back and fix</button>
              <button type="button" className="btn-ghost text-amber-800" disabled={save.isPending}
                      onClick={() => { setWastageWarn(false); save.mutate(); }}>
                Save anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <form className="space-y-4" onSubmit={onSubmit}>
        {/* One disabled fieldset rather than a prop per input: a field added
            later cannot accidentally stay editable for a read-only role.
            On a reissue or a Club Order a second layer of fieldsets locks
            everything except the Client Requested Quantity and the Wastage,
            plus the clubbed POs on a Club Order — see the split lower down.
            display:contents on the inner fieldsets keeps the parent's grid
            layout intact while still propagating the disabled state to
            descendant form controls. */}
        <fieldset disabled={!canEditJob} className="space-y-4 disabled:opacity-100">
        <Section title="Source PO">
          <fieldset disabled={locked} className="contents">
          <div className="md:col-span-2">
            <label className="label">PO Number *</label>
            <input
              className="input mb-2"
              placeholder="Type to search by PO number or job name…"
              value={poQuery}
              onChange={(e) => setPoQuery(e.target.value)}
            />
            <select className="input" value={form.poRefNumber}
              onChange={(e) => selectPO(e.target.value)}>
              <option value="">— select a PO —</option>
              {/* POs without a job card first, so the divider falls between the two groups. */}
              {[...(recentPos.data ?? [])].sort((a, b) => (a.hasJc ?? 0) - (b.hasJc ?? 0)).map((p, i, arr) => {
                const prevHasJc = i > 0 ? arr[i - 1]?.hasJc : 0;
                return (<Fragment key={p.id}>
                  {p.hasJc === 1 && !prevHasJc && <option disabled>──── already has JC ────</option>}
                  <option value={p.id}>{poOptionText(p)}</option>
                </Fragment>);
              })}
              {form.poRefNumber && !recentPos.data?.find((p) => p.id === form.poRefNumber) && (
                <option value={form.poRefNumber}>{form.poRefNumber} (existing)</option>
              )}
            </select>
            {recentPos.data && recentPos.data.length === 0 && (
              <p className="mt-1 text-xs text-amber-700">
                {poQuery.trim()
                  ? 'No PO without a job card matches that search. A PO whose job card is closed shows only when you type its full PO number.'
                  : <>All recent POs already have a Job Order. <a href="/purchase-orders/new" className="underline">Create a new PO</a> first.</>}
              </p>
            )}
            {recentPos.data && recentPos.data.length > 0 && (
              <p className="mt-1 text-xs text-slate-500">
                {(() => {
                  const noJc = recentPos.data.filter((p) => !p.hasJc).length;
                  const withJc = recentPos.data.length - noJc;
                  return `${noJc} PO${noJc === 1 ? '' : 's'} without a job card${withJc ? ` · ${withJc} already ${withJc === 1 ? 'has' : 'have'} one` : ''}`;
                })()}{recentPos.data.length >= 100 ? ' — narrow the search to find an older PO' : ''}
              </p>
            )}
          </div>
          </fieldset>

          {/* Clubbed PO section - open in Club Order mode, locked on a reissue. */}
          <fieldset disabled={reissueLocked} className="contents">
          {(additionalPos.length > 0 || form.poRefNumber) && (
            <div className="md:col-span-3">
              {additionalPos.length > 0 && (
                <div className="mb-2">
                  <label className="label text-blue-700">Clubbed POs ({1 + additionalPos.length} total)</label>
                  {cardProducts.length > 1 && (
                    <div className="mb-1.5 text-sm text-slate-700">
                      Products on this card: <b>{cardProducts.map((p) => p.name).join(' + ')}</b>
                      {club && club.mismatches.length === 0 && club.missing.length === 0 && (
                        <span className="ml-2 text-xs font-medium text-emerald-700">Product Master specs match — both names print on the job card</span>
                      )}
                    </div>
                  )}
                  <div className="space-y-1">
                    {additionalPos.map((lp) => (
                      <div key={lp.id} className="flex items-center gap-2 rounded border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm">
                        <span className="flex-1 truncate">{lp.label}</span>
                        {(() => {
                          const p = productOfPo(lp.id);
                          if (!p?.productId || !club?.main || p.productId.toUpperCase() === club.main.productId.toUpperCase()) return null;
                          const differs = club.mismatches.some((m) => m.productId.toUpperCase() === p.productId!.toUpperCase());
                          return (
                            <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold ${differs ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-800'}`}
                              title={differs ? 'Its Product Master specs differ from the main product' : 'Another product — its Product Master specs match'}>
                              {p.productName ?? p.productId} · {differs ? 'specs differ' : 'specs match ✓'}
                            </span>
                          );
                        })()}
                        <span className="text-xs text-slate-500">Qty: {Number(lp.quantity || 0).toLocaleString('en-IN')}</span>
                        {lp.date && <span className="text-xs text-slate-500">{new Date(lp.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}</span>}
                        {/* Club Order attaches POs: only one attached now can come off again. */}
                        {canEditJob && (!isClubEdit || !cardPoIds.has(lp.id)) && (
                          <button type="button" onClick={() => removeLinkedPo(lp.id)}
                            className="ml-1 text-red-400 hover:text-red-600 text-xs font-bold">x</button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {canEditJob && !showAddPo && (
                <button type="button" onClick={() => setShowAddPo(true)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline">
                  + Add another PO (clubbed order)
                </button>
              )}
              {showAddPo && (
                <div className="mt-1 rounded border border-blue-200 bg-blue-50/50 p-2">
                  <input className="input mb-1 text-sm" placeholder="Search PO to add…"
                    value={addPoQuery} onChange={(e) => setAddPoQuery(e.target.value)} autoFocus />
                  <select className="input text-sm" value="" onChange={(e) => { if (e.target.value) addLinkedPo(e.target.value); }}>
                    <option value="">— select PO to add —</option>
                    {addPoResults.data
                      ?.filter((p) => p.id !== form.poRefNumber && !additionalPos.some((a) => a.id === p.id))
                      .map((p) => <option key={p.id} value={p.id}>{poOptionText(p)}</option>)}
                  </select>
                  <button type="button" onClick={() => { setShowAddPo(false); setAddPoQuery(''); }}
                    className="mt-1 text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                </div>
              )}
              {clubRefused ? (
                <ClubDiffs check={clubRefused} adding={clubRefused.adding} onDismiss={() => setClubRefused(null)} />
              ) : club && (club.mismatches.length > 0 || club.missing.length > 0) && (
                // A card clubbed before this check: it keeps its POs, but
                // they can't change until the specs match.
                <ClubDiffs check={club} />
              )}
            </div>
          )}
          </fieldset>

          <fieldset disabled={locked} className="contents">
          <F label="Job PO Date" type="date" value={form.jobPoDate} onChange={(v) => update('jobPoDate', v)} />
          <F label="Job Order Date (auto)" type="date" value={form.jobOrderDate} onChange={(v) => update('jobOrderDate', v)} />
          <F label="Job Name (from PO)" value={form.jobName} onChange={(v) => update('jobName', v)} />
          <div>
            <label className="label">Order Type</label>
            <select className="input" value={form.orderType} onChange={(e) => update('orderType', e.target.value)} disabled={!canEditJob}>
              <option value="">—</option>
              <option value="New">New</option>
              <option value="Change">Change</option>
              <option value="Repeat">Repeat</option>
              <option value="Proofing">Proofing</option>
            </select>
          </div>
          </fieldset>
        </Section>

        <Section title="Quantities">
          {/* Inside the Quantities section only Client Requested Quantity and
              Wastage stay editable in Reissue mode; everything else is
              wrapped in a contents-display fieldset so the disable
              propagates without collapsing the grid layout. */}
          <fieldset disabled={locked} className="contents">
            <Read label={additionalPos.length > 0 ? 'Primary PO Quantity' : 'PO Quantity (from PO)'} value={form.poQuantity} />
            {additionalPos.length > 0 && (
              <Read label="Combined PO Quantity (all POs)"
                value={String((Number(form.poQuantity) || 0) + additionalPos.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0))} />
            )}
            {!isEdit && poDetails.data && (poDetails.data.jobCardedQty ?? 0) > 0 && (
              <div className="md:col-span-2">
                <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs">
                  <span className="font-medium text-slate-700">Already job-carded:</span>{' '}
                  {(poDetails.data.jobCardedQty ?? 0).toLocaleString('en-IN')} ({poDetails.data.jobCards} JC{(poDetails.data.jobCards ?? 0) !== 1 ? 's' : ''})
                  {' '}<span className="font-semibold text-blue-700">
                    Remaining: {Math.max(0, (Number(form.poQuantity) || 0) - (poDetails.data.jobCardedQty ?? 0)).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            )}
          </fieldset>
          {/* The two free inputs, on a reissue and a Club Order too. */}
          <F label="Client Requested Quantity" type="number" value={form.clientRequestedQuantity} onChange={(v) => update('clientRequestedQuantity', v)} />
          <fieldset disabled={locked} className="contents">
            <F label="Ups" type="number" value={form.ups} onChange={(v) => update('ups', v)} />
            <Read label="Total Sheet (Boxes ÷ Ups)" value={String(totals.baseSheets)} />
          </fieldset>
          <div>
            <F label="Wastage (extra sheets)" type="number" value={form.wastage} onChange={(v) => update('wastage', v)} />
            {wastageBlocked ? (
              <p className="mt-1 text-xs font-semibold text-red-700">
                ✕ {n(extraBoxes)} extra boxes is over {Math.round(WASTAGE_MAX_PCT * 100)}% of {n(clientQty)} ordered — the most wastage that saves is {n(maxSavableSheets)} sheets. The card will not be saved.
              </p>
            ) : over20 ? (
              <p className="mt-1 text-xs font-medium text-amber-700">
                ⚠ {n(extraBoxes)} extra boxes is over {Math.round(WASTAGE_MAX_PCT * 100)}% of {n(clientQty)} ordered — allowed, as the wastage is {n(minSheets)} sheets or less{onBig ? ` (${onBig})` : ''}. Check it.
              </p>
            ) : wastageHigh && (
              <p className="mt-1 text-xs font-medium text-amber-700">
                ⚠ High: {wastageSheets.toLocaleString('en-IN')} sheets — limit {wastageLimit.toLocaleString('en-IN')} ({wastageRule}).
              </p>
            )}
          </div>
          <fieldset disabled={locked} className="contents">
            <Read label="Total Sheet incl. Wastage" value={String(totals.totalSheetWithWastage)} />
            <Read label="Total Boxes" value={String(totals.totalBoxes)} />
          </fieldset>
        </Section>

        <fieldset disabled={locked} className="contents">{/* reissue / club lock: after-quantities */}

        <Section title="Specification">
          <Sel label="Paper Type" value={form.paperType} onChange={(v) => update('paperType', v)} options={papers.data ?? []} />
          <Sel label="Sheet Size" value={form.sheetSize} onChange={(v) => update('sheetSize', v)} options={sheets.data ?? []} />
          <Sel label="Lamination Type" value={form.laminationType} onChange={(v) => update('laminationType', v)} options={finishes.data ?? []} />
          <F label="Sheet Cut Size L (mm)" value={form.sheetCutSizeLength} onChange={(v) => update('sheetCutSizeLength', v)} />
          <F label="Sheet Cut Size W (mm)" value={form.sheetCutSizeWidth} onChange={(v) => update('sheetCutSizeWidth', v)} />
        </Section>

        <Section title="Die & Machine">
          <F label="Die Cutting (Die No)" value={form.dieCutting} onChange={(v) => update('dieCutting', v)} />
          <F label="Selected Die / Artwork" value={form.selectedDie} onChange={(v) => update('selectedDie', v)} />
          <Sel label="Machine Front" value={form.machine || form.machineFront} onChange={(v) => { update('machine', v); update('machineFront', v); }} options={machineOptions(machines.data, allMachines.data, form.machine || form.machineFront)} />
          <PantoneAutocomplete label="Front Color" value={form.machineFrontColor} onChange={(v) => update('machineFrontColor', v)} />
          {/* Front 2 and Back are the card's own: they start from the product and,
              once saved, change only here — editing the product does not move them. */}
          <Sel label="Machine Front 2" value={form.machineFront2} onChange={(v) => update('machineFront2', v)} options={machineOptions(machines.data, allMachines.data, form.machineFront2)} />
          <PantoneAutocomplete label="Front 2 Color" value={form.machineFront2Color} onChange={(v) => update('machineFront2Color', v)} />
          <Sel label="Machine Back" value={form.machineBack} onChange={(v) => update('machineBack', v)} options={machineOptions(machines.data, allMachines.data, form.machineBack)} />
          <PantoneAutocomplete label="Back Color" value={form.machineBackColor} onChange={(v) => update('machineBackColor', v)} />
          <F label="A/W Material No" value={form.artWNo} onChange={(v) => update('artWNo', v)} />
        </Section>
        <Section title="Remarks">
          <div className="md:col-span-2">
            <textarea className="input min-h-[80px]" value={form.remarks} onChange={(e) => update('remarks', e.target.value)} />
          </div>
        </Section>

        {revisions.data && revisions.data.length > 0 && (
          <Section title="Quantity Revision History">
            <div className="md:col-span-3 overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b bg-slate-50 text-slate-600">
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Revised By</th>
                    <th className="px-3 py-2 font-medium">Old Quantity</th>
                    <th className="px-3 py-2 font-medium">New Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {revisions.data.map((r, i) => (
                    <tr key={i} className="border-b last:border-b-0 hover:bg-slate-50">
                      <td className="px-3 py-2">{r.RevisedOn ? new Date(r.RevisedOn).toLocaleString() : ''}</td>
                      <td className="px-3 py-2">{r.UserName || 'System'}</td>
                      <td className="px-3 py-2 text-slate-500">{r.OldQuantity || '0'}</td>
                      <td className="px-3 py-2 font-semibold text-brand">{r.NewQuantity || '0'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {canSeeHistory && history.data && history.data.length > 0 && (
          <Section title="Job Card History">
            <div className="md:col-span-3 overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b bg-slate-50 text-slate-600">
                    <th className="px-3 py-2 font-medium whitespace-nowrap">Changed On</th>
                    <th className="px-3 py-2 font-medium whitespace-nowrap">Changed By</th>
                    <th className="px-3 py-2 font-medium whitespace-nowrap">Job Date</th>
                    <th className="px-3 py-2 font-medium whitespace-nowrap">Boxes</th>
                    <th className="px-3 py-2 font-medium whitespace-nowrap">Sheets</th>
                    <th className="px-3 py-2 font-medium whitespace-nowrap">Machine</th>
                    <th className="px-3 py-2 font-medium whitespace-nowrap">Status</th>
                    <th className="px-3 py-2 font-medium whitespace-nowrap">Reason</th>
                    <th className="px-3 py-2 font-medium whitespace-nowrap">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.map((h) => (
                    <tr key={h.History_ID} className="border-b last:border-b-0 hover:bg-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap">{h.Changed_On ? new Date(h.Changed_On).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</td>
                      <td className="px-3 py-2">{h.UserName || 'System'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{h.JobOrder_date ? new Date(h.JobOrder_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                      <td className="px-3 py-2">{h.Total_Boxes || '0'}</td>
                      <td className="px-3 py-2">{h.TotalSheet_withWastage || '0'}</td>
                      <td className="px-3 py-2">{allMachines.data?.find(m => m.id === h.Machine)?.name || h.Machine || ''}</td>
                      <td className="px-3 py-2">{h.Job_Staus || ''}</td>
                      <td className="px-3 py-2 max-w-[200px] truncate" title={h.Change_Reason || ''}>{h.Change_Reason || ''}</td>
                      <td className="px-3 py-2 max-w-[200px] truncate" title={h.Remarks || ''}>{h.Remarks || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        </fieldset>{/* /reissue / club lock: after-quantities */}
        </fieldset>{/* /canEditJob */}

        {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

        <div className="flex justify-end gap-2">
          <Link to={listReturnUrl('/job-orders')} className="btn-ghost">
            {canEditJob ? 'Cancel' : 'Back'}
          </Link>
          {canEditJob && (
            <button type="submit" className="btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

/**
 * Why POs of different products can't share the card: each product's specs
 * that differ from the main one.  Red when a PO was just refused; amber for a
 * card that clubbed them before this check.
 */
function ClubDiffs({ check, adding, onDismiss }: { check: ClubCheck & { adding?: string }; adding?: string; onDismiss?: () => void }) {
  const refused = !!onDismiss;
  const addingNo = adding ? check.pos.find((p) => p.poId === adding)?.poNumber ?? adding : null;
  const mainName = check.main?.productName ?? 'the main product';
  return (
    <div className={`mt-2 rounded border px-3 py-2 text-sm ${refused ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
      <div className="font-semibold">
        {refused
          ? addingNo ? `PO ${addingNo} can't be clubbed onto this job card.` : "These POs can't share one job card."
          : 'The products on this card have different Product Master specs.'}
      </div>
      {check.mismatches.map((m) => (
        <div key={m.productId} className="mt-2">
          <div><b>{m.productName}</b> differs from <b>{mainName}</b> in:</div>
          <div className="mt-1 overflow-x-auto">
            <table className="text-xs bg-white/70 rounded">
              <thead>
                <tr className="text-left">
                  <th className="px-2 py-1 font-semibold">Spec</th>
                  <th className="px-2 py-1 font-semibold">{mainName}</th>
                  <th className="px-2 py-1 font-semibold">{m.productName}</th>
                </tr>
              </thead>
              <tbody>
                {m.diffs.map((d) => (
                  <tr key={d.label} className="border-t border-black/5">
                    <td className="px-2 py-1 font-medium whitespace-nowrap">{d.label}</td>
                    <td className="px-2 py-1">{d.main}</td>
                    <td className="px-2 py-1">{d.other}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {check.missing.length > 0 && (
        <div className="mt-2">Product {check.missing.join(', ')} is not in the Product Master — pick the right product on the PO.</div>
      )}
      <div className="mt-2 text-xs">
        {refused
          ? 'Different products share a job card only when their Product Master specs are the same (name, size, K to K size, design no., art / mat no., plate, BP and shade card no. may differ). Correct the Product Master, or make a separate job card.'
          : 'It was clubbed before this check, so it keeps these POs — but adding or changing POs needs matching specs.'}
      </div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} className="mt-1 text-xs underline">Dismiss</button>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="card-header">{title}</div>
      <div className="card-body grid gap-4 md:grid-cols-3">{children}</div>
    </div>
  );
}
function F({ label, value, onChange, type = 'text', step }:
  { label: string; value: string; onChange: (v: string) => void; type?: string; step?: string }) {
  const displayValue = type === 'date' ? normalizeDate(value) : value;
  return (
    <div>
      <label className="label">{label}</label>
      <input
        className="input"
        type={type}
        value={displayValue}
        onChange={(e) => onChange(e.target.value)}
        step={step ?? (type === 'number' ? 'any' : undefined)}
      />
    </div>
  );
}
function Read({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input bg-slate-50" value={value} readOnly />
    </div>
  );
}
function Sel({ label, value, onChange, options }:
  { label: string; value: string; onChange: (v: string) => void; options: LookupOption[] }) {
  return (
    <div>
      <label className="label">{label}</label>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        {value && !options.find((o) => o.id === value) && <option value={value}>{value} (legacy)</option>}
      </select>
    </div>
  );
}
